/**
 * svgArrange — pure SVG mutation helpers for Arrange mode.
 *
 * All functions in this module operate on live SVGElements (no string
 * serialisation, no remounts).
 *
 * Design rules:
 *  1. Default (version 0) content is read directly from the base SVGElement
 *     via `extractDefaultVariantNode` — no duplicated v0 cache.
 *  2. Caller threads an optional `prevVariants` array so we only touch the
 *     compartments whose version actually changed (DOM diff; keeps the
 *     mounted SVG stable — no zoom/pan/scroll reset).
 *
 * Imported by `PlanRendererArrangeMode` and unit-tested in jsdom.
 */

// ── Pure DOM helpers ──────────────────────────────────────────────────────────

/**
 * Returns the live `<g number="N">` node from a base floor-plan SVGElement.
 *
 * Used as the source for "version 0" (default arrangement) — the base SVG
 * already contains every compartment's default equipments/caseworks layout,
 * so version 0 never needs a network round-trip.
 *
 * @param {SVGElement} baseSvgElement
 * @param {number}     compartmentNumber
 * @returns {SVGGElement|null}
 */
export function extractDefaultVariantNode(baseSvgElement, compartmentNumber) {
  if (!baseSvgElement) return null
  return baseSvgElement.querySelector(
    `.compartments > g[number="${compartmentNumber}"]`,
  )
}

/**
 * Replaces the `<g.equipments>` and `<g.caseworks>` children of a single
 * compartment group in `targetSvg` with the corresponding children from
 * `sourceGroup`. Also copies the `transform` attribute of `<text.tag>`
 * (the compartment label) when both sides expose one — variants may shift
 * the label to make room for new furniture.
 *
 * The source children are cloned (`cloneNode(true)`) so the caller is free
 * to reuse `sourceGroup` across multiple invocations (e.g. cached variant
 * nodes used by several mounts).
 *
 * Paint order: caseworks are inserted *before* equipments so equipments
 * stay on top.
 *
 * @param {SVGElement}   targetSvg     — clone of the base SVG (mutated in place)
 * @param {SVGGElement}  sourceGroup   — `<g number="N">` source node
 * @param {number}       compartmentNumber
 */
export function applyVariantToCompartment(targetSvg, sourceGroup, compartmentNumber) {
  if (!targetSvg || !sourceGroup) return

  const targetGroup = targetSvg.querySelector(
    `.compartments > g[number="${compartmentNumber}"]`,
  )
  if (!targetGroup) return

  // Sync text.tag transform (label may shift between variants).
  const targetTag = targetGroup.querySelector('text.tag')
  const sourceTag = sourceGroup.querySelector('text.tag')
  if (targetTag && sourceTag) {
    const t = sourceTag.getAttribute('transform')
    if (t != null) targetTag.setAttribute('transform', t)
  }

  const targetEquipments = targetGroup.querySelector('g.equipments')
  const targetCaseworks  = targetGroup.querySelector('g.caseworks')
  const sourceEquipments = sourceGroup.querySelector('g.equipments')
  const sourceCaseworks  = sourceGroup.querySelector('g.caseworks')

  // ── Caseworks first (paint order: caseworks below equipments) ──
  if (targetCaseworks) targetGroup.removeChild(targetCaseworks)
  if (sourceCaseworks) {
    const cloned = sourceCaseworks.cloneNode(true)
    if (targetEquipments) {
      targetGroup.insertBefore(cloned, targetEquipments)
    } else {
      targetGroup.appendChild(cloned)
    }
  }

  // ── Equipments ──
  if (sourceEquipments) {
    const cloned = sourceEquipments.cloneNode(true)
    if (targetEquipments) {
      targetGroup.replaceChild(cloned, targetEquipments)
    } else {
      targetGroup.appendChild(cloned)
    }
  } else if (targetEquipments) {
    targetGroup.removeChild(targetEquipments)
  }
}

/**
 * Applies the user's full variant selection to `targetSvg` by walking each
 * compartment and forwarding to `applyVariantToCompartment`.
 *
 * For version 0 the source is read from `baseSvgElement` (default arrangement).
 * For version ≥ 1 the source is fetched lazily via `getVariantNode(n, v)`.
 *
 * When `prevVariants` is supplied, compartments whose version did not change
 * are skipped — this is the DOM diff that keeps the on-screen SVG stable
 * across rapid clicks (no zoom/pan reset, no flicker).
 *
 * @param {object} params
 * @param {SVGElement}                  params.targetSvg        — the live, mounted SVG clone
 * @param {SVGElement}                  params.baseSvgElement   — pristine base from useProductSvg
 * @param {number[]}                    params.selectedVariants — index-aligned with compartments (number-1)
 * @param {(n: number, v: number) => SVGGElement|null} params.getVariantNode
 * @param {number[]|null}              [params.prevVariants]    — when given, diff vs this array
 */
export function applyCompartmentVariants({
  targetSvg,
  baseSvgElement,
  selectedVariants,
  getVariantNode,
  prevVariants = null,
}) {
  if (!targetSvg || !baseSvgElement || !Array.isArray(selectedVariants)) return

  for (let i = 0; i < selectedVariants.length; i++) {
    const compartmentNumber = i + 1
    const version = selectedVariants[i]

    // Skip unchanged compartments when caller provided a previous snapshot.
    if (prevVariants && prevVariants[i] === version) continue

    const source = version === 0
      ? extractDefaultVariantNode(baseSvgElement, compartmentNumber)
      : getVariantNode(compartmentNumber, version)

    // Source may legitimately be null (variant fetch failed) — skip silently;
    // the previous content stays on screen. Logging happens in the hook.
    if (!source) continue

    applyVariantToCompartment(targetSvg, source, compartmentNumber)
  }
}

// ── Class helpers (consumed by Arrange-mode interactions) ─────────────────────

/**
 * Toggles `noVariantsClass` on every `path.compartment` inside `container`
 * based on whether the compartment has at least one alternative variant
 * (variantsCount[number-1] > 1). Compartments without alternatives are
 * non-interactive — CSS uses this class to switch the cursor and dim them.
 *
 * Safe to call multiple times — each call resets the class to the current
 * `variantsCount` snapshot.
 *
 * @param {HTMLElement|null} container
 * @param {number[]}         variantsCount
 * @param {string}           noVariantsClass
 */
export function syncNoVariantsClass(container, variantsCount, noVariantsClass) {
  if (!container || !Array.isArray(variantsCount) || !noVariantsClass) return
  const paths = container.querySelectorAll('path.compartment')
  for (const path of paths) {
    const raw = path.getAttribute('number')
    const n = raw != null ? Number(raw) : null
    const hasVariants = n != null && variantsCount[n - 1] > 1
    path.classList.toggle(noVariantsClass, !hasVariants)
  }
}

/**
 * Toggles `hoveredClass` on every `path.compartment` so that exactly the
 * compartment with the given number carries the class (or none, when
 * `hoveredCompartmentId` is null).
 *
 * @param {HTMLElement|null} container
 * @param {number|null}      hoveredCompartmentId
 * @param {string}           hoveredClass
 */
export function syncHoveredCompartmentClass(container, hoveredCompartmentId, hoveredClass) {
  if (!container || !hoveredClass) return
  const paths = container.querySelectorAll('path.compartment')
  for (const path of paths) {
    const raw = path.getAttribute('number')
    const n = raw != null ? Number(raw) : null
    path.classList.toggle(hoveredClass, n === hoveredCompartmentId)
  }
}
